import { BridgeFlagsManager, LocalFlagsManager } from '@databases/flags';
import { activityLogger } from '@databases/logs/activity-logger';
import { LocalPanelManager } from '@databases/panel/managers';
import { PluginsManager, PluginKey, type PluginsState } from '@databases/plugins';
import { type ContainerOverrideOptions } from '@databases/service-container';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

export interface ModeConfig {
  dependencies: Omit<ContainerOverrideOptions, 'Intl'>;
}

const pluginsState: PluginsState = {
  [PluginKey.CELL_SELECTION]: () => true,
  [PluginKey.COMMENTS]: () => true,
  [PluginKey.CONTEXT_MENU]: () => true,
  [PluginKey.DATABASE_ACTIONS]: () => true,
  [PluginKey.EDIT_CONTENT]: () => true,
  [PluginKey.INLINE_DATABASE_NAME]: () => false,
  [PluginKey.QUICK_CALCULATIONS]: () => true,
  [PluginKey.VIEW_SETTINGS_ACTIONS]: () => true,
  [PluginKey.VIEW_SETTINGS_BUTTONS]: () => true,
};

/**
 * Creates a base mode configuration object with default dependency providers.
 *
 * @example
 * ```typescript
 * const config = createBaseModeConfig({
 *   plugins: pluginsState,
 *   dependencies: {
 *     CustomService: () => new CustomServiceImpl()
 *   }
 * });
 * ```
 */
export function createBaseModeConfig({
  plugins,
  dependencies = {},
}: {
  plugins: PluginsState;
  dependencies?: Partial<Omit<ContainerOverrideOptions, 'Intl' | 'PluginsManager'>>;
}): ModeConfig {
  return {
    dependencies: {
      FlagsManager: () => {
        return isFeatureFlagEnabled('database-frontend-flags-over-bridge')
          ? new BridgeFlagsManager()
          : new LocalFlagsManager();
      },
      PluginsManager: () => new PluginsManager(plugins),
      PanelManager: () => new LocalPanelManager(activityLogger),
      ...dependencies,
    },
  };
}

export function createDefaultModeConfig(): ModeConfig {
  return createBaseModeConfig({ plugins: pluginsState });
}
